import { useGrowthBook } from '@growthbook/growthbook-react';
import React, { Suspense, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { useGetUser } from './api/useUser';
import { Loading } from '@repo/ui/components/Loading';
import SignIn from './pages/auth/SignIn';
import SignOut from './pages/auth/SignOut';
import ErrorPage from './pages/error/ErrorPage';
import { useEmbeddedAuth } from './contexts/embedded-auth/useEmbeddedAuth';

const Affiliations = React.lazy(
  () => import('./pages/affiliations/Affiliations')
);
const TermsAndConditionsOverlay = React.lazy(() =>
  import('./components/auth/TermsAndConditionsOverlay').then((m) => ({
    default: m.TermsAndConditionsOverlay,
  }))
);
const Welcome = React.lazy(() =>
  import('./pages/auth/Welcome').then((m) => ({ default: m.Welcome }))
);
const Callback = React.lazy(() =>
  import('./pages/auth/Callback').then((m) => ({ default: m.Callback }))
);
const Authoring = React.lazy(() => import('./pages/authoring/Authoring'));
const Branding = React.lazy(() => import('./pages/branding/Branding'));
const Faq = React.lazy(() => import('./pages/faq/Faq'));
const Homepage = React.lazy(() => import('./pages/home/Homepage'));
const ManageDepartments = React.lazy(
  () => import('./pages/manage-departments/ManageDepartments')
);
const DeclineInvitation = React.lazy(() =>
  import('./pages/manuscript-review-invitation/DeclineInvitation').then(
    (m) => ({ default: m.DeclineInvitation })
  )
);
const ManuscriptReviewInvitiation = React.lazy(() =>
  import('./pages/manuscript-review-invitation/ManuscriptReviewInvitation').then(
    (m) => ({ default: m.ManuscriptReviewInvitiation })
  )
);
const Messages = React.lazy(() => import('./pages/messages/Messages'));
const Profile = React.lazy(() =>
  import('./pages/profile/Profile').then((m) => ({ default: m.Profile }))
);
const PublicationOverview = React.lazy(
  () => import('./pages/publication-overview/PublicationOverview')
);
const Reviewing = React.lazy(() => import('./pages/reviewing/Reviewing'));
const RedeemInvitation = React.lazy(
  () => import('./pages/redeem-invitation/RedeemInvitation')
);
const Reviews = React.lazy(() => import('./pages/reviews/Reviews'));
const Statistics = React.lazy(() => import('./pages/statistics/Statistics'));
const AdminSettings = React.lazy(
  () => import('./pages/superadmin-settings/AdminSettings')
);
const Announcements = React.lazy(
  () => import('./pages/announcements/Announcements')
);
const Unauthorized = React.lazy(() =>
  import('./pages/error/Unauthorized').then((m) => ({
    default: m.Unauthorized,
  }))
);
const UniversityTermsAndConditions = React.lazy(() =>
  import('./pages/university-terms-and-conditions/UniversityTermsAndConditions').then(
    (m) => ({ default: m.UniversityTermsAndConditions })
  )
);

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useEmbeddedAuth();
  const { data: user, isLoading: isLoadingUser } = useGetUser(isAuthenticated);
  const growthbook = useGrowthBook();

  useEffect(() => {
    if (user && growthbook) {
      growthbook.setAttributes({
        userId: user.id,
        email: user.email,
        role: user.affiliations.find(
          (affiliation) =>
            affiliation.university.id === user.university?.id &&
            affiliation.status === 'active'
        )?.role,
        universityId: user.university?.id,
        universityName: user.university?.name,
        universitySlug: user.university?.slug,
      });
    }
  }, [user, growthbook]);

  // Check if user needs to accept T&Cs
  const needsToAcceptTerms =
    user && !user.metadata?.termsAndConditionsAcceptedAt && !isLoadingUser;

  return (
    <>
      <Suspense fallback={<Loading size="fullScreen" />}>
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route
            path="/reviews"
            element={
              <ProtectedRoute
                permissions={['review']}
                alsoAllowIf={user?.hasReviews}
              >
                <Reviews />
              </ProtectedRoute>
            }
          />
          <Route
            path="/authoring/*"
            element={
              <ProtectedRoute
                permissions={['publish']}
                alsoAllowIf={user?.isAuthoring}
              >
                <Authoring />
              </ProtectedRoute>
            }
          />
          <Route path="/reviewing/:reviewId" element={<Reviewing />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/profile" element={<Profile />} />
          <Route
            path="/publication-overview"
            element={
              <ProtectedRoute
                permissions={['publish']}
                alsoAllowIf={user?.isAuthoring}
              >
                <PublicationOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage-departments"
            element={
              <ProtectedRoute permissions={['access-manage-departments-page']}>
                <ManageDepartments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage-people"
            element={
              <ProtectedRoute permissions={['access-manage-affiliations-page']}>
                <Affiliations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/branding"
            element={
              <ProtectedRoute permissions={['access-manage-branding-page']}>
                <Branding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/statistics"
            element={
              <ProtectedRoute permissions={['read-university-statistics']}>
                <Statistics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-settings"
            element={
              <ProtectedRoute permissions={['update-manuscript-doi']}>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/announcements"
            element={
              <ProtectedRoute permissions={['manage-announcements']}>
                <Announcements />
              </ProtectedRoute>
            }
          />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/callback" element={<Callback />} />
          <Route path="/sign-out" element={<SignOut />} />
          <Route
            path="/invitation/:invitationId"
            element={<RedeemInvitation />}
          />
          <Route
            path="/review-invitation/:id"
            element={<ManuscriptReviewInvitiation />}
          />
          <Route
            path="/review-invitation/declined"
            element={<DeclineInvitation />}
          />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route
            path="/university-terms-and-conditions"
            element={<UniversityTermsAndConditions />}
          />
          <Route path="*" element={<ErrorPage />} />
        </Routes>
      </Suspense>
      {needsToAcceptTerms && !isLoadingUser && <TermsAndConditionsOverlay />}
    </>
  );
};

export default AppRoutes;
